/**
 * Cancer Benefit Definitions
 *
 * Uses builder functions for standard type/amount pairs.
 * Custom cases (hospice with amount+duration, preExisting with custom editor) remain manual.
 * Enum values provided via typeEnumValues parameter using OpenAPI types.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  BaseCancerBenefitInitialDiagnosisWaitingPeriodType,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  NotDisclosed,
  NotOffered,
  RateGuaranteeTypeEnum,
  RfpCancerAmountBenefitType,
  RfpCancerScheduleBenefitType,
  RfpInclusionStatusEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildCommissionBenefits,
  buildPreExistingConditionBenefitPair,
  buildTypeAmountBenefitPair,
  buildTypeDurationBenefitPair,
} from "../shared/definitions/builders";

import * as cancerCellSpecs from "./cellSpecs";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    networkDependent: false,
    benefitPlanLevel: true,
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "diagnosisBenefit",
    typeLabel: "Diagnosis Benefit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerAmountBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "The dollar benefit available when a cancer diagnosis is made",
    amountDescription: "Diagnosis benefit dollar amount",
    amountDefaultValue: 1,
    isRequired: true,
  }),
  ...buildTypeDurationBenefitPair({
    prefix: "initialDiagnosisWaitingPeriod",
    typeLabel: "Initial Diagnosis Waiting Period",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      BaseCancerBenefitInitialDiagnosisWaitingPeriodType,
    ),
    durationCell: sharedCellSpecs.numberField,
    typeDescription:
      "The time a member must wait in order to receive benefits, after receiving a cancer diagnosis",
    durationDescription: "The duration of the waiting period",
    durationDefaultValue: 1,
    disabledValues: [NotDisclosed],
    isRequired: true,
    durationCellVariants: {
      options: {
        [BaseCancerBenefitInitialDiagnosisWaitingPeriodType.days]: {
          ...sharedCellSpecs.numberField,
          suffix: "Days",
        },
        [BaseCancerBenefitInitialDiagnosisWaitingPeriodType.months]: {
          ...sharedCellSpecs.numberField,
          suffix: "Months",
        },
      },
      default: sharedCellSpecs.numberField,
    },
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "radiationTherapyChemo",
    typeLabel: "Radiation Therapy / Chemo",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerAmountBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "The benefit available for radiation/chemo. This can be a schedule or a flat dollar amount",
    amountDescription: "Radiation therapy / chemo dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "bloodPlasmaPlatelets",
    typeLabel: "Blood/Plasma/Platelets",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerAmountBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "The benefit available for services related to blood/plasma/platelets",
    amountDescription: "Blood/plasma/platelets dollar amount",
    amountDefaultValue: 1,
  }),
  hospiceType: {
    label: "Hospice",
    description:
      "The daily amount available when on hospice. Also the limit of days per lifetime that this benefit is available",
    cell: cancerCellSpecs.cancerHospiceType,
    networkDependent: false,
    refreshesCells: ["hospiceAmount", "hospiceDuration"],
  },
  hospiceAmount: {
    label: "Amount",
    description: "Hospice dollar amount per day",
    cell: {
      ...sharedCellSpecs.dollarAmount,
      suffix: "per day",
    },
    nested: true,
    networkDependent: false,
    defaultValue: 1,
    editableWhen: {
      dependsOn: "hospiceType",
      disabledValues: [
        "notIncluded",
        "notApplicable",
        NotDisclosed,
        NotOffered,
      ],
    },
  },
  hospiceDuration: {
    label: "Duration",
    description:
      "The limit of days per lifetime that this benefit is available. 0 is unlimited",
    cell: {
      ...sharedCellSpecs.numberField,
      suffix: "days",
    },
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "hospiceType",
      disabledValues: [
        "notIncluded",
        "notApplicable",
        NotDisclosed,
        NotOffered,
        "dollarAmount",
      ],
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "hospitalConfinementInitial",
    typeLabel: "Hospital Confinement  - Initial",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerAmountBenefitType,
    ),
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for this service, including any conditional requirements",
    amountCell: sharedCellSpecs.dollarAmount,
    amountDescription: "Hospital confinement - initial dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "icuConfinementInitial",
    typeLabel: "ICU Confinement - Initial",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerAmountBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for this service, including any conditional requirements",
    amountDescription: "ICU confinement - initial dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "skinCancer",
    typeLabel: "Skin Cancer",
    typeCell: cancerCellSpecs.cancerScheduleBenefitType,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerScheduleBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "The benefit for skin cancer treatment",
    amountDescription: "Skin cancer dollar amount",
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "surgicalBenefit",
    typeLabel: "Surgical Benefit",
    typeCell: cancerCellSpecs.cancerScheduleBenefitType,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerScheduleBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "The benefit for surgical procedures",
    amountDescription: "Surgical benefit dollar amount",
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    amountDefaultValue: 1,
  }),
  ...buildPreExistingConditionBenefitPair({
    typeDescription:
      "Indicates the amount of months before effective date AND after effective date participant needs to be illness free to claim benefit",
    monthsDetailDescription:
      "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues: ["waived", "none", NotDisclosed, NotOffered],
    monthsDetailDefaultValue: "01/01",
  }),
  portability: {
    label: "Portability",
    description: "Ability to continue coverage after employment ends",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "wellnessBenefit",
    typeLabel: "Wellness Benefit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpCancerAmountBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "The wellness benefit available",
    amountDescription: "Wellness benefit dollar amount",
    amountDefaultValue: 1,
  }),
  waiverOfPremium: {
    label: "Waiver of Premium",
    description:
      "Indicates if premium payments will be waived in the case of a critical illness or serious injury",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Type of annual open enrollment available",
    cell: sharedCellSpecs.annualOpenEnrollmentType,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  volumeIllustratedFrom: mpOnlyRow(
    "Volume illustrated from _",
    "volume_illustrated_from",
  ),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),

  // Extended (non-v5) field (CD-1665/CD-2075/CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
} as const;
